/**
 * Requests Service
 * ================
 * All Firestore operations for borrow requests.
 *
 * State machine (enforced by Firestore Rules AND here):
 *   pending → approved → active → returned
 *   pending → cancelled  (by borrower or lender)
 *   active  → extended   (borrower requests, lender approves)
 *   active  → overdue    (set by scheduled Cloud Function)
 *
 * Security: Firestore Rules enforce transitions server-side.
 * This service adds client-side guards for fast UX feedback.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import { db }        from "@/lib/firebase";
import type { BorrowRequest, RequestStatus, ReminderMethod, ApiResult } from "@/types";

const REQUESTS = "requests";

// ─── CONVERTERS ──────────────────────────────────────────────────────────────
function docToRequest(id: string, data: Record<string, unknown>): BorrowRequest {
  return {
    id,
    bookId:                   String(data["bookId"]   ?? ""),
    bookTitle:                String(data["bookTitle"] ?? ""),
    bookCoverUrl:             data["bookCoverUrl"] as string | null ?? null,
    borrowerId:               String(data["borrowerId"] ?? ""),
    borrowerName:             String(data["borrowerName"] ?? ""),
    lenderId:                 String(data["lenderId"]  ?? ""),
    status:                   (data["status"] as RequestStatus) ?? "pending",
    requestedDurationWeeks:   Number(data["requestedDurationWeeks"] ?? 1),
    dueDate:                  (data["dueDate"] as Timestamp)?.toDate() ?? null,
    extensionCount:           Number(data["extensionCount"] ?? 0),
    maxExtensions:            Number(data["maxExtensions"] ?? 2),
    deliveryType:             (data["deliveryType"] as "meetup" | "deliver") ?? "meetup",
    deliveryAddress:          data["deliveryAddress"] as string | undefined,
    meetupLocation:           data["meetupLocation"]  as string | undefined,
    reminderMethods:          (data["reminderMethods"] as ReminderMethod[]) ?? ["email"],
    message:                  data["message"]  as string | undefined,
    rating:                   data["rating"]   as number | undefined,
    review:                   data["review"]   as string | undefined,
    createdAt:                (data["createdAt"] as Timestamp)?.toDate() ?? new Date(),
    updatedAt:                (data["updatedAt"] as Timestamp)?.toDate() ?? new Date(),
    returnedAt:               (data["returnedAt"] as Timestamp)?.toDate() ?? undefined,
  };
}

// ─── CREATE REQUEST ──────────────────────────────────────────────────────────
export interface CreateRequestPayload {
  bookId:                  string;
  bookTitle:               string;
  bookCoverUrl:            string | null;
  lenderId:                string;
  borrowerId:              string;
  borrowerName:            string;
  requestedDurationWeeks:  number;
  deliveryType:            "meetup" | "deliver";
  deliveryAddress?:        string;
  meetupLocation?:         string;
  reminderMethods:         ReminderMethod[];
  message?:                string;
}

export async function createRequest(
  payload: CreateRequestPayload
): Promise<ApiResult<string>> {
  try {
    // Prevent self-borrowing (belt-and-suspenders — Rules also block this)
    if (payload.borrowerId === payload.lenderId) {
      return { ok: false, code: "self-borrow", message: "You cannot borrow your own book." };
    }

    // Check book is still available
    const bookSnap = await getDoc(doc(db, "books", payload.bookId));
    if (!bookSnap.exists() || bookSnap.data()?.["isAvailable"] !== true) {
      return { ok: false, code: "not-available", message: "This book is no longer available." };
    }

    const ref = await addDoc(collection(db, REQUESTS), {
      bookId:                 payload.bookId,
      bookTitle:              payload.bookTitle,
      bookCoverUrl:           payload.bookCoverUrl,
      borrowerId:             payload.borrowerId,
      borrowerName:           payload.borrowerName,
      lenderId:               payload.lenderId,
      status:                 "pending",
      requestedDurationWeeks: payload.requestedDurationWeeks,
      dueDate:                null,           // set when lender approves
      extensionCount:         0,
      maxExtensions:          2,
      deliveryType:           payload.deliveryType,
      ...(payload.deliveryAddress && { deliveryAddress: payload.deliveryAddress }),
      ...(payload.meetupLocation  && { meetupLocation:  payload.meetupLocation }),
      reminderMethods:        payload.reminderMethods,
      ...(payload.message && { message: payload.message }),
      createdAt:              serverTimestamp(),
      updatedAt:              serverTimestamp(),
    });
    return { ok: true, data: ref.id };
  } catch (err) {
    console.error("[createRequest]", err);
    return { ok: false, code: "create-failed", message: "Failed to send request. Please try again." };
  }
}

// ─── APPROVE REQUEST ─────────────────────────────────────────────────────────
export async function approveRequest(requestId: string): Promise<ApiResult<void>> {
  try {
    const snap = await getDoc(doc(db, REQUESTS, requestId));
    if (!snap.exists()) return { ok: false, code: "not-found", message: "Request not found." };
    const data = snap.data();
    if (data["status"] !== "pending") return { ok: false, code: "invalid-status", message: "This request has already been processed." };

    // Calculate due date
    const weeks = Number(data["requestedDurationWeeks"] ?? 1);
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + weeks * 7);

    await updateDoc(doc(db, REQUESTS, requestId), {
      status:    "approved",
      dueDate:   Timestamp.fromDate(dueDate),
      updatedAt: serverTimestamp(),
    });

    // Mark book as unavailable
    await updateDoc(doc(db, "books", data["bookId"]), {
      isAvailable: false,
      updatedAt:   serverTimestamp(),
    });

    return { ok: true, data: undefined };
  } catch (err) {
    console.error("[approveRequest]", err);
    return { ok: false, code: "approve-failed", message: "Failed to approve request." };
  }
}

// ─── DECLINE / CANCEL REQUEST ────────────────────────────────────────────────
export async function cancelRequest(
  requestId: string,
  reason?: string
): Promise<ApiResult<void>> {
  try {
    await updateDoc(doc(db, REQUESTS, requestId), {
      status:    "cancelled",
      ...(reason && { cancellationReason: reason }),
      updatedAt: serverTimestamp(),
    });
    return { ok: true, data: undefined };
  } catch (err) {
    console.error("[cancelRequest]", err);
    return { ok: false, code: "cancel-failed", message: "Failed to cancel request." };
  }
}

// ─── REQUEST EXTENSION ───────────────────────────────────────────────────────
export async function requestExtension(requestId: string): Promise<ApiResult<void>> {
  try {
    const snap = await getDoc(doc(db, REQUESTS, requestId));
    if (!snap.exists()) return { ok: false, code: "not-found", message: "Request not found." };
    const data = snap.data();

    if (!["active", "extended"].includes(data["status"])) {
      return { ok: false, code: "invalid-status", message: "Can only extend active borrows." };
    }
    if (data["extensionCount"] >= data["maxExtensions"]) {
      return { ok: false, code: "max-extensions", message: "Maximum extensions reached. Contact the owner directly." };
    }

    const currentDue = (data["dueDate"] as Timestamp).toDate();
    const newDue = new Date(currentDue);
    newDue.setDate(newDue.getDate() + 7);

    await updateDoc(doc(db, REQUESTS, requestId), {
      status:         "extended",
      dueDate:        Timestamp.fromDate(newDue),
      extensionCount: (data["extensionCount"] as number) + 1,
      updatedAt:      serverTimestamp(),
    });
    return { ok: true, data: undefined };
  } catch (err) {
    console.error("[requestExtension]", err);
    return { ok: false, code: "extend-failed", message: "Failed to extend borrow." };
  }
}

// ─── MARK RETURNED ───────────────────────────────────────────────────────────
export async function markReturned(requestId: string): Promise<ApiResult<void>> {
  try {
    const snap = await getDoc(doc(db, REQUESTS, requestId));
    if (!snap.exists()) return { ok: false, code: "not-found", message: "Request not found." };

    await updateDoc(doc(db, REQUESTS, requestId), {
      status:     "returned",
      returnedAt: serverTimestamp(),
      updatedAt:  serverTimestamp(),
    });

    // Re-mark book as available
    await updateDoc(doc(db, "books", snap.data()["bookId"]), {
      isAvailable: true,
      updatedAt:   serverTimestamp(),
    });

    return { ok: true, data: undefined };
  } catch (err) {
    console.error("[markReturned]", err);
    return { ok: false, code: "return-failed", message: "Failed to mark as returned." };
  }
}

// ─── FETCH REQUESTS ──────────────────────────────────────────────────────────
export async function fetchIncomingRequests(
  lenderId: string
): Promise<ApiResult<BorrowRequest[]>> {
  try {
    const q = query(
      collection(db, REQUESTS),
      where("lenderId", "==", lenderId),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    const requests = snap.docs.map(d => docToRequest(d.id, d.data() as Record<string, unknown>));
    return { ok: true, data: requests };
  } catch (err) {
    console.error("[fetchIncomingRequests]", err);
    return { ok: false, code: "fetch-failed", message: "Failed to load requests." };
  }
}

export async function fetchMyBorrows(
  borrowerId: string
): Promise<ApiResult<BorrowRequest[]>> {
  try {
    const q = query(
      collection(db, REQUESTS),
      where("borrowerId", "==", borrowerId),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    const requests = snap.docs.map(d => docToRequest(d.id, d.data() as Record<string, unknown>));
    return { ok: true, data: requests };
  } catch (err) {
    console.error("[fetchMyBorrows]", err);
    return { ok: false, code: "fetch-failed", message: "Failed to load your borrows." };
  }
}

// ─── REAL-TIME LISTENER ──────────────────────────────────────────────────────
/**
 * Subscribe to live updates for a user's requests.
 * Used in the Requests inbox — updates in real time when lender approves.
 */
export function subscribeToRequests(
  userId: string,
  role:   "borrower" | "lender",
  cb:     (requests: BorrowRequest[]) => void
): Unsubscribe {
  const field = role === "borrower" ? "borrowerId" : "lenderId";
  const q = query(
    collection(db, REQUESTS),
    where(field, "==", userId),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (snap) => {
    const requests = snap.docs.map(d => docToRequest(d.id, d.data() as Record<string, unknown>));
    cb(requests);
  });
}
